/**
 * Ролевая тренировка — экран этапа S10, §10.12 ТЗ.
 *
 * Сотрудник выбирает ситуацию, модель играет гостя, сотрудник отвечает
 * так, как ответил бы в зале. Через двенадцать реплик разговор
 * закрывается сам и приходит оценка по шести критериям заказчика.
 *
 * **Четыре вещи, из-за которых экран устроен именно так:**
 *
 * - **фраза о приватности стоит сверху, до выбора сценария** (§10.15,
 *   пункт приёмки 54). Человек, не знающий, что разговор не увидит
 *   руководитель, отвечает осторожно — и тренируется не тому;
 * - **исчерпанная квота — предложение, а не серая кнопка** (§10.12).
 *   Текст «чем заняться вместо» пишет сервер, экран его не сочиняет;
 * - **оценка тренировки — не оценка сотрудника.** Числа показываются
 *   рядом с ориентиром «что считается хорошей работой»: цифра без
 *   ориентира читается как отметка;
 * - **последняя реплика сверху не переворачивается.** Разговор
 *   читается как переписка, сверху вниз, а поле ответа — под ним: в
 *   отличие от помощника, здесь важен ход беседы, а не последний ответ.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { RoleplayHome, RoleplaySession } from '../api';

const HOME_KEY = ['study', 'mentor', 'roleplay'];

function problemText(error: unknown): string {
  // Сообщение сервера доходит как `message` ошибки (`ApiError.detail`):
  // «квота закончилась», «нет связи с моделью» — нормативные тексты, и
  // второй их набор в браузере разошёлся бы с ботом.
  if (error instanceof Error && error.message) return error.message;
  return 'Не получилось — похоже, пропала связь. Попробуйте ещё раз.';
}

function Score({ session, home }: { session: RoleplaySession; home: RoleplayHome }) {
  if (!session.score) {
    return (
      <Card title="Тренировка закончена">
        {session.summary.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </Card>
    );
  }
  return (
    <Card title={`Итог: ${session.score.average.toFixed(1).replace('.', ',')} из 5`}>
      <ul className="roleplay-scores">
        {home.criteria.map((criterion) => (
          <li key={criterion.code}>
            <span>{criterion.title}</span>
            <b>{session.score?.values[criterion.code] ?? '—'}</b>
          </li>
        ))}
      </ul>
      {session.summary.map((line) => (
        <p key={line}>{line}</p>
      ))}
      {session.scenario.success_note && (
        <p className="faint">Что считается хорошей работой: {session.scenario.success_note}</p>
      )}
      <p className="faint">
        Это оценка разговора, а не ваша. Руководителю уходит только динамика по критериям.
      </p>
    </Card>
  );
}

function Dialog({
  session,
  home,
  onChanged,
}: {
  session: RoleplaySession;
  home: RoleplayHome;
  onChanged: (session: RoleplaySession | null) => void;
}) {
  const [draft, setDraft] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const reply = useMutation({
    mutationFn: (text: string) => studyApi.roleplayReply(session.id, text),
    onMutate: () => setProblem(null),
    onSuccess: (updated) => {
      setDraft('');
      onChanged(updated);
    },
    onError: (error) => setProblem(problemText(error)),
  });
  const finish = useMutation({
    mutationFn: () => studyApi.roleplayFinish(session.id),
    onMutate: () => setProblem(null),
    onSuccess: (updated) => onChanged(updated),
    onError: (error) => setProblem(problemText(error)),
  });

  const text = draft.trim();
  const busy = reply.isPending || finish.isPending;
  const submit = () => {
    if (text && !busy) reply.mutate(text);
  };
  const scoresOnFinish = session.replies_made >= session.min_replies_for_score;

  return (
    <>
      <Card title={session.scenario.name}>
        <ol className="roleplay-dialog" aria-label="Разговор">
          {session.turns.map((turn) => (
            <li
              key={turn.ord}
              className={`roleplay-turn roleplay-turn--${turn.role === 'agent' ? 'guest' : 'me'}`}
            >
              <span className="roleplay-turn__who">{turn.role === 'agent' ? 'Гость' : 'Вы'}</span>
              {turn.body}
            </li>
          ))}
        </ol>
        {session.note && <p className="faint">({session.note})</p>}
      </Card>

      {session.finished ? (
        <>
          <Score session={session} home={home} />
          <Button variant="primary" onClick={() => onChanged(null)}>
            К списку ситуаций
          </Button>
        </>
      ) : (
        <Card title="Ваш ответ гостю">
          <textarea
            className="search ask"
            rows={3}
            value={draft}
            disabled={busy}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Скажите так, как сказали бы в зале"
          />
          <div className="chips">
            <Button variant="primary" disabled={!text || busy} onClick={submit}>
              {reply.isPending ? 'Гость думает…' : 'Ответить'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => finish.mutate()}>
              {scoresOnFinish ? 'Закончить и получить оценку' : 'Прервать'}
            </Button>
          </div>
          <p className="faint">
            Осталось реплик: {session.turns_left} из {home.max_turns}. После последней
            разговор закроется сам и придёт оценка.
          </p>
          {problem && <p className="answer failure">{problem}</p>}
        </Card>
      )}
    </>
  );
}

export function Roleplay() {
  const queryClient = useQueryClient();
  const home = useQuery({ queryKey: HOME_KEY, queryFn: studyApi.roleplayHome });
  const [session, setSession] = useState<RoleplaySession | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: (scenarioId: number) => studyApi.roleplayStart(scenarioId),
    onMutate: () => setProblem(null),
    onSuccess: (started) => setSession(started),
    onError: (error) => setProblem(problemText(error)),
  });

  if (home.isLoading) return <Loading />;
  if (home.error || !home.data) {
    return <ErrorState error={home.error} onRetry={() => home.refetch()} />;
  }
  const data = home.data;
  const current = session ?? data.open_session;

  const changed = (updated: RoleplaySession | null) => {
    setSession(updated);
    // Квота и история изменились — их пересчитывает сервер.
    if (updated === null || updated.finished) {
      void queryClient.invalidateQueries({ queryKey: HOME_KEY });
    }
  };

  return (
    <div className="roleplay">
      <p className="mentor__freshness" role="note">
        {data.privacy_notice}.
      </p>

      {!data.available ? (
        <Empty title="Тренировки недоступны" text={data.reason} />
      ) : current ? (
        <Dialog session={current} home={data} onChanged={changed} />
      ) : (
        <>
          {data.quota && <p className={data.quota.can_start ? 'faint' : 'answer'}>{data.quota.text}</p>}
          {problem && <p className="answer failure">{problem}</p>}

          {data.scenarios.length === 0 ? (
            <Empty title="Ситуаций пока нет" text={data.reason} />
          ) : (
            <Card title="Выберите ситуацию">
              <p className="faint">
                Гостя играет ИИ. Отвечайте так, как ответили бы в зале, — через{' '}
                {data.max_turns} реплик придёт оценка по шести критериям.
              </p>
              <ul className="roleplay-scenarios">
                {data.scenarios.map((scenario) => (
                  <li key={scenario.id}>
                    <Button
                      variant="quiet"
                      disabled={!data.quota?.can_start || start.isPending}
                      onClick={() => start.mutate(scenario.id)}
                    >
                      {scenario.name}
                    </Button>
                  </li>
                ))}
              </ul>
              {start.isPending && <p className="faint">Гость входит в зал…</p>}
            </Card>
          )}

          {data.history.length > 0 && (
            <Card title="Мои тренировки">
              <ul className="roleplay-history">
                {data.history.map((row) => (
                  <li key={row.session_id}>
                    <span>{row.scenario}</span>
                    <span className="faint">
                      {new Date(row.started_at).toLocaleDateString('ru-RU')}
                      {' · '}
                      {row.average === null
                        ? 'без оценки'
                        : `${row.average.toFixed(1).replace('.', ',')} из 5`}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
